import { useState, useEffect, useRef, useCallback } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { ChevronRight, Home, User, Clock, RefreshCw, ChevronDown, Zap, PenTool, CalendarDays, BarChart3, Search, X, Flame } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar';
import { AppSidebar } from '@/components/AppSidebar';
import { useD2DData } from '@/hooks/useD2DData';
import { useAuth } from '@/hooks/useAuth';
import { useAreaPresence } from '@/hooks/useAreaPresence';
import { toast } from '@/hooks/use-toast';
import { D2DAreaList } from '@/components/d2d/D2DAreaList';
import { D2DStreetList } from '@/components/d2d/D2DStreetList';
import { D2DHouseList } from '@/components/d2d/D2DHouseList';
import { D2DUnitList } from '@/components/d2d/D2DUnitList';
import { supabase } from '@/integrations/supabase/client';
import { D2DLeadsList } from '@/components/d2d/D2DLeadsList';
import { D2DErrorBoundary } from '@/components/d2d/D2DErrorBoundary';
import CustomerManagement from '@/pages/CustomerManagement';
import { D2DFollowUpCalendar } from '@/components/d2d/D2DFollowUpCalendar';
import { useLocation, useSearchParams } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { OfflineIndicator } from '@/components/OfflineIndicator';
import { D2DSuccessStatistics } from '@/components/d2d/D2DSuccessStatistics';
import { D2DUserSettings } from '@/components/d2d/D2DUserSettings';
import { DashboardHeader } from '@/components/shared/DashboardHeader';
import type { FollowUp } from '@/hooks/useDashboardData';
import type {
  D2DArea, 
  D2DStreet, 
  D2DHouse, 
  D2DNavigation,
  D2DCreateStreetData,
  D2DCreateHouseData,
  D2DTag
} from '@/types/d2d';

// Constants for scroll position persistence
const SCROLL_POSITION_KEY = 'running-list-scroll-position';
const NAVIGATION_STATE_KEY = 'running-list-navigation-state';

export default function RunningList() {
  const { user } = useAuth();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const loadingRef = useRef(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const followUpListRef = useRef<HTMLDivElement>(null);

  // Initialize sidebar state from localStorage, default to true (expanded)
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
    const saved = localStorage.getItem('sidebarState');
    return saved ? JSON.parse(saved) : true;
  });

  // Save sidebar state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('sidebarState', JSON.stringify(isSidebarOpen));
  }, [isSidebarOpen]);


  const {
    loading,
    isOnline,
    isSyncing,
    queueLength,
    areas,
    streets,
    houses,
    units,
    preferences,
    loadAreas,
    loadStreets,
    loadHouses,
    loadUnits,
    createStreet,
    createHouse,
    createUnit,
    createUnitsBatch,
    updateUnitTags,
    updateUnitTagsOnly,
    updateUnitName,
    createUnitFollowUp,
    addNote,
    deleteNote,
    deleteUnit,
    deleteHouse,
    deleteStreet,
    editStreetName,
    savePreferences,
    reorganizeFollowups,
    reorganizeFollowupsByLocation,
    addHouseNote,
    updateHouseNote,
    deleteHouseNote,
    // Offline caching
    isCaching,
    cacheArea,
    clearCache,
    cachedAreaIds,
    checkAreaCached,
    getCachedAreaUnits,
  } = useD2DData();

  const [navigation, setNavigation] = useState<D2DNavigation>({
    currentLevel: 'areas'
  });
  const [isRestoringNavigation, setIsRestoringNavigation] = useState(false);
  const [navigationRestored, setNavigationRestored] = useState(false);
  const [selectedLeadType, setSelectedLeadType] = useState<'hot' | 'followup' | 'na' | null>(null);
  const [globalLeadCounts, setGlobalLeadCounts] = useState({ hot: 0, followup: 0, na: 0 });
  const [focusedUnitId, setFocusedUnitId] = useState<string | null>(null);
  const [showCustomerDetails, setShowCustomerDetails] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);
  const [reorganizeAreaId, setReorganizeAreaId] = useState<string>('');
  const [reorganizeStreetId, setReorganizeStreetId] = useState<string>('');
  const [isReorganizeOpen, setIsReorganizeOpen] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isTimelineOpen, setIsTimelineOpen] = useState(() => {
    const saved = localStorage.getItem('timelineOpenState');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [showCalendarView, setShowCalendarView] = useState(false);
  const [showStatistics, setShowStatistics] = useState(false);
  const [isReorganizingFollowups, setIsReorganizingFollowups] = useState(false);
  const [isReorganizingAllUsers, setIsReorganizingAllUsers] = useState(false);
  const [todayFollowUps, setTodayFollowUps] = useState<Array<{
    id: string;
    time: string;
    address: string;
    name?: string;
    source: 'running_list' | 'd2d_units';
    manual_reminder?: boolean;
    isHot?: boolean;
    isKT?: boolean;
  }>>([]);
  const timelineScrollRef = useRef<HTMLDivElement>(null);
  const [areaSearchQuery, setAreaSearchQuery] = useState('');

  // Area presence tracking for collaborative visibility (must be after navigation state)
  const { otherUsers, getUsersInArea, getUsersInStreet, getUsersInHouse } = useAreaPresence({
    userId: user?.id,
    currentAreaId: navigation.selectedArea?.id,
    currentAreaName: navigation.selectedArea?.name,
    currentStreetId: navigation.selectedStreet?.id,
    currentStreetName: navigation.selectedStreet?.name,
    currentHouseId: navigation.selectedHouse?.id,
    currentHouseNumber: navigation.selectedHouse?.house_number,
  });

  // User check after all hooks are defined to prevent hooks order issues
  if (!user) {
    return <div>Please log in to continue</div>;
  }

  useEffect(() => {
    localStorage.setItem('timelineOpenState', JSON.stringify(isTimelineOpen));
  }, [isTimelineOpen]);

  // Reset restoration state when component mounts
  useEffect(() => {
    setIsRestoringNavigation(false);
    setNavigationRestored(false);
  }, []);

  // Handle URL parameter for direct area navigation
  const urlAreaHandledRef = useRef(false);
  useEffect(() => {
    const areaId = searchParams.get('area');
    // Only handle once and when areas are loaded
    if (areaId && areas.length > 0 && !urlAreaHandledRef.current) {
      const targetArea = areas.find(a => a.id === areaId);
      if (targetArea) {
        urlAreaHandledRef.current = true;
        // Navigate directly to the area
        setNavigation({ 
          currentLevel: 'streets', 
          selectedArea: targetArea 
        });
        loadStreets(targetArea.id);
      }
    }
  }, [searchParams, areas]); // Remove loadStreets from deps to prevent infinite loop

  // Update clock every second for accurate time display
  useEffect(() => {
    // Set current time immediately on mount
    setCurrentTime(new Date());
    
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Auto-scroll follow-ups list to current time
  useEffect(() => {
    const scrollToCurrentTime = () => {
      if (!followUpListRef.current || todayFollowUps.length === 0) return;
      
      const now = new Date();
      const currentTime = now.getHours() * 60 + now.getMinutes();
      
      // Find the closest follow-up to current time
      let closestIndex = 0;
      let smallestDiff = Infinity;
      
      todayFollowUps.forEach((followUp, index) => {
        const [hours, minutes] = followUp.time.split(':').map(Number);
        const followUpTime = hours * 60 + minutes;
        const diff = Math.abs(followUpTime - currentTime);
        
        if (diff < smallestDiff) {
          smallestDiff = diff;
          closestIndex = index;
        }
      });
      
      // Scroll to the closest follow-up (center it in view)
      const scrollContainer = followUpListRef.current;
      const items = scrollContainer.children;
      
      if (items[closestIndex]) {
        const item = items[closestIndex] as HTMLElement;
        const containerHeight = scrollContainer.clientHeight;
        const itemHeight = item.offsetHeight;
        const itemTop = item.offsetTop;
        
        const scrollTop = itemTop - (containerHeight / 2) + (itemHeight / 2);
        
        scrollContainer.scrollTo({
          top: Math.max(0, scrollTop),
          behavior: 'smooth'
        });
      }
    };
    
    // Start auto-scroll after 45 seconds delay
    const initialTimeout = setTimeout(() => {
      scrollToCurrentTime();
      
      // Then continue every minute
      const interval = setInterval(scrollToCurrentTime, 60000);
      
      return () => clearInterval(interval);
    }, 45000);
    
    return () => {
      clearTimeout(initialTimeout);
    };
  }, [todayFollowUps, currentTime]);

  // Center timeline on current hour
  useEffect(() => {
    const centerTimeline = () => {
      if (!timelineScrollRef.current) return;
      
      const currentHour = currentTime.getHours();
      const scrollContainer = timelineScrollRef.current;
      
      // Calculate scroll position to center current hour
      const itemWidth = 88;
      const startHour = 9;
      const currentHourIndex = Math.max(0, currentHour - startHour);
      const containerWidth = scrollContainer.clientWidth;
      const scrollLeft = (currentHourIndex * itemWidth) - (containerWidth / 2) + (itemWidth / 2);
      
      scrollContainer.scrollTo({
        left: Math.max(0, scrollLeft),
        behavior: 'smooth'
      });
    };
    
    // Start timeline centering after 45 seconds delay
    const initialTimeout = setTimeout(() => {
      centerTimeline();
    }, 45000);
    
    return () => {
      clearTimeout(initialTimeout);
    };
  }, [currentTime]);

  // Load today's follow-ups
  const followUpsLoadingRef = useRef(false);
  const loadTodayFollowUps = async () => {
    if (!user) return;

    // Prevent overlapping requests (realtime can trigger multiple times quickly)
    if (followUpsLoadingRef.current) return;
    followUpsLoadingRef.current = true;

    try {
      const today = new Date();
      const dayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      const dayEnd = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59);

      // 1) Fetch base datasets in parallel (NO deep joins here to avoid statement timeouts)
      const [runningListRes, manualUnitsRes, autoUnitsRes] = await Promise.all([
        supabase
          .from('running_list_entries')
          .select('id, street, house_number, apartment_unit, follow_up_scheduled, notes')
          .eq('user_id', user.id)
          .gte('follow_up_scheduled', dayStart.toISOString())
          .lte('follow_up_scheduled', dayEnd.toISOString())
          .not('follow_up_scheduled', 'is', null),

        supabase
          .from('d2d_units')
          .select('id, unit_name, next_reminder, follow_up_notes, manual_reminder, tags, house_id')
          .eq('user_id', user.id)
          .eq('manual_reminder', true)
          .gte('next_reminder', dayStart.toISOString())
          .lte('next_reminder', dayEnd.toISOString())
          .not('next_reminder', 'is', null),

        supabase
          .from('d2d_units')
          .select('id, unit_name, next_reminder, follow_up_notes, manual_reminder, tags, house_id')
          .eq('user_id', user.id)
          .eq('manual_reminder', false)
          .gte('next_reminder', dayStart.toISOString())
          .lte('next_reminder', dayEnd.toISOString())
          .not('next_reminder', 'is', null),
      ]);

      if (runningListRes.error) throw runningListRes.error;
      if (manualUnitsRes.error) throw manualUnitsRes.error;
      if (autoUnitsRes.error) throw autoUnitsRes.error;

      const runningListData = runningListRes.data || [];
      const manualUnitsData = manualUnitsRes.data || [];
      const autoUnitsData = autoUnitsRes.data || [];

      // 2) Load only the required house/street/area context for the involved house_ids
      const houseIds = Array.from(
        new Set([
          ...manualUnitsData.map(u => u.house_id).filter(Boolean),
          ...autoUnitsData.map(u => u.house_id).filter(Boolean),
        ])
      ) as string[];

      const houseMap = new Map<
        string,
        {
          house_number: string;
          street_name: string;
          plz?: string | null;
          area_is_active?: boolean | null;
        }
      >();

      if (houseIds.length > 0) {
        const { data: housesData, error: housesError } = await supabase
          .from('d2d_houses')
          .select(
            `id, house_number, d2d_streets!inner(name, plz, d2d_areas!inner(is_active))`
          )
          .in('id', houseIds);

        if (housesError) throw housesError;

        (housesData || []).forEach((h: any) => {
          houseMap.set(h.id, {
            house_number: h.house_number,
            street_name: h.d2d_streets?.name,
            plz: h.d2d_streets?.plz ?? null,
            area_is_active: h.d2d_streets?.d2d_areas?.is_active ?? null,
          });
        });
      }

      const followUps: Array<{
        id: string;
        time: string;
        address: string;
        name?: string;
        source: 'running_list' | 'd2d_units';
        manual_reminder?: boolean;
        isHot?: boolean;
        isKT?: boolean;
      }> = [];

      // Process running list entries
      runningListData.forEach(entry => {
        const time = new Date(entry.follow_up_scheduled).toLocaleTimeString('de-DE', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        });
        const address = `${entry.street} ${entry.house_number}`;
        const name = entry.apartment_unit || address;
        followUps.push({
          id: `rl_${entry.id}`,
          time,
          address,
          name,
          source: 'running_list',
          manual_reminder: true,
          isHot: false,
          isKT: false,
        });
      });

      // Helper to add a unit
      const pushUnit = (unit: any) => {
        const tags = (unit.tags as string[]) || [];
        const hasDisabledTag = tags.includes('KI') || tags.includes('NO') || tags.includes('WIN');
        if (hasDisabledTag) return;

        const ctx = houseMap.get(unit.house_id);
        if (!ctx?.street_name || !ctx?.house_number) return;

        const isHot = tags.includes('HOT');
        const isKT = tags.includes('KT');

        const time = new Date(unit.next_reminder).toLocaleTimeString('de-DE', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        });
        const address = `${ctx.street_name} ${ctx.house_number}`;
        const name = unit.unit_name || address;

        followUps.push({
          id: `d2d_${unit.id}`,
          time,
          address,
          name,
          source: 'd2d_units',
          manual_reminder: unit.manual_reminder,
          isHot,
          isKT,
        });
      };

      // Manual reminders: always visible (even if area inactive)
      manualUnitsData.forEach(pushUnit);

      // Auto reminders: only from active areas
      autoUnitsData.forEach(u => {
        const ctx = houseMap.get(u.house_id);
        if (!ctx) return;
        if (ctx.area_is_active !== true) return;
        pushUnit(u);
      });

      // Sort by time
      followUps.sort((a, b) => a.time.localeCompare(b.time));
      setTodayFollowUps(followUps);
    } catch (error) {
      console.error("Error loading today's follow-ups:", error);
    } finally {
      followUpsLoadingRef.current = false;
    }
  };

  // Function to shorten address while keeping house number visible
  const shortenAddress = (address: string) => {
    const parts = address.trim().split(' ');
    if (parts.length < 2) return address;
    
    const houseNumber = parts[parts.length - 1];
    const streetName = parts.slice(0, -1).join(' ');
    
    // If street name is longer than 15 characters, truncate it
    if (streetName.length > 15) {
      const shortened = streetName.substring(0, 12) + '...';
      return `${shortened} ${houseNumber}`;
    }
    
    return address;
  };
  // Setup Realtime subscriptions for instant updates - SINGLE subscription for follow-ups
  useEffect(() => {
    if (!user) return;

    console.log('🔔 Setting up follow-up Realtime subscriptions...');
    
    // Subscribe to d2d_units changes for follow-up updates only
    const unitsChannel = supabase
      .channel('d2d_units_followups')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'd2d_units',
          filter: `user_id=eq.${user.id}`
        },
        () => {
          // Only update follow-ups list (lead counts handled by separate subscription)
          loadTodayFollowUps();
        }
      )
      .subscribe();

    // Subscribe to running_list_entries changes
    const runningListChannel = supabase
      .channel('running_list_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'running_list_entries',
          filter: `user_id=eq.${user.id}`
        },
        () => {
          loadTodayFollowUps();
        }
      )
      .subscribe();

    return () => {
      console.log('🔕 Cleaning up follow-up Realtime subscriptions...');
      supabase.removeChannel(unitsChannel);
      supabase.removeChannel(runningListChannel);
    };
  }, [user]); // CRITICAL: Only depend on user, not navigation!

  // Update overdue follow-ups then load today's follow-ups
  const updateOverdueAndLoadFollowUps = async () => {
    if (!user) return;
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        loadTodayFollowUps();
        return;
      }

      // Call edge function to reschedule any overdue follow-ups
      await supabase.functions.invoke('update-overdue-followups', {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        }
      });
    } catch (error) {
      console.error('Error updating overdue follow-ups:', error);
    } finally {
      // Load follow-ups after updating
      loadTodayFollowUps();
    }
  };

  // Load follow-ups on mount without rescheduling today's missed ones
  useEffect(() => {
    if (user) {
      loadTodayFollowUps();
    }
  }, [user]);

  // Function to reorganize follow-ups for all users (Admin function)
  const handleReorganizeAllUsersFollowups = async () => {
    if (!user?.id) return;
    
    console.log('🚀 Button clicked - starting reorganization...');
    
    const confirmed = window.confirm(
      'Möchten Sie wirklich alle AUTOMATISCHEN Follow-ups (NA-Tags) für alle Benutzer im System neu organisieren?\n\nManuelle KT-Follow-ups bleiben unverändert.\n\nDiese Aktion kann nicht rückgängig gemacht werden.'
    );
    
    if (!confirmed) {
      console.log('❌ User canceled reorganization');
      return;
    }
    
    setIsReorganizingAllUsers(true);
    try {
      console.log('🔄 Starting system-wide follow-up reorganization...');
      console.log('🔑 User ID:', user.id);
      
      const { data, error } = await supabase.functions.invoke('reorganize-all-followups', {
        headers: {
          Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
        }
      });
      
      console.log('📡 Raw response from reorganize-all-followups:', { data, error });
      
      if (error) {
        console.error('❌ Error reorganizing all users follow-ups:', error);
        toast({
          title: "Fehler",
          description: `System-weite Follow-up Reorganisation fehlgeschlagen: ${error.message}`,
          variant: "destructive",
        });
        return;
      }
      
      console.log('✅ System-wide follow-up reorganization completed:', data);
      
      toast({
        title: "System-weite Reorganisation abgeschlossen",
        description: `${data?.processedUsers || 0} Benutzer und ${data?.processedUnits || 0} Follow-ups wurden erfolgreich neu organisiert.`,
      });
      
      // Force reload data to reflect changes
      console.log('🔄 Reloading data...');
      await Promise.all([
        loadGlobalLeadCounts(),
        loadTodayFollowUps()
      ]);
      console.log('✅ Data reloaded');
      
    } catch (error) {
      console.error('❌ Unexpected error during system-wide reorganization:', error);
      toast({
        title: "Fehler",
        description: `Unerwarteter Fehler: ${error instanceof Error ? error.message : 'Unbekannter Fehler'}`,
        variant: "destructive",
      });
    } finally {
      setIsReorganizingAllUsers(false);
    }
  };

  // Function to reorganize follow-ups (clear all + reschedule in one click)
  const handleReorganizeFollowups = async () => {
    if (!user?.id) return;
    
    setIsReorganizingFollowups(true);
    try {
      // Step 1: Clear all automatic follow-ups
      console.log('🧹 Step 1: Clearing all automatic follow-ups...');
      toast({
        title: "Schritt 1/2",
        description: "Lösche alle automatischen Follow-ups...",
      });
      
      const { data: clearData, error: clearError } = await supabase.functions.invoke('clear-followups');
      
      if (clearError) {
        console.error('❌ Error clearing follow-ups:', clearError);
        toast({
          title: "Fehler",
          description: "Follow-ups konnten nicht gelöscht werden.",
          variant: "destructive",
        });
        return;
      }
      
      console.log('✅ Cleared follow-ups:', clearData);
      
      // Step 2: Reschedule all follow-ups
      console.log('🔄 Step 2: Auto-rescheduling all follow-ups...');
      toast({
        title: "Schritt 2/2",
        description: `${clearData?.cleared || 0} gelöscht. Plane jetzt neu...`,
      });
      
      const { data: rescheduleData, error: rescheduleError } = await supabase.functions.invoke('auto-reschedule-followups', {
        body: { manualTrigger: true }
      });
      
      if (rescheduleError) {
        console.error('❌ Error rescheduling follow-ups:', rescheduleError);
        toast({
          title: "Fehler",
          description: "Follow-ups konnten nicht neu geplant werden.",
          variant: "destructive",
        });
        return;
      }
      
      console.log('✅ Rescheduled follow-ups:', rescheduleData);
      
      toast({
        title: "✅ Follow-ups komplett neu geplant",
        description: `${clearData?.cleared || 0} gelöscht, ${rescheduleData?.rescheduled || 0} neu geplant.`,
      });
      
      // Reload global lead counts to reflect changes
      loadGlobalLeadCounts();
      // Reload today's follow-ups to show updated times
      loadTodayFollowUps();
      
    } catch (error) {
      console.error('❌ Unexpected error during reorganization:', error);
      toast({
        title: "Fehler",
        description: "Ein unerwarteter Fehler ist aufgetreten.",
        variant: "destructive",
      });
    } finally {
      setIsReorganizingFollowups(false);
    }
  };

  // Save scroll position and navigation state when leaving the page
  useEffect(() => {
    const saveScrollPosition = () => {
      if (scrollContainerRef.current) {
        const scrollTop = scrollContainerRef.current.scrollTop;
        sessionStorage.setItem(SCROLL_POSITION_KEY, scrollTop.toString());
        console.log('💾 Saved scroll position:', scrollTop);
      }
    };

    const saveNavigationState = () => {
      const stateToSave = {
        navigation,
        selectedLeadType,
        focusedUnitId
      };
      sessionStorage.setItem(NAVIGATION_STATE_KEY, JSON.stringify(stateToSave));
      console.log('💾 Saved navigation state:', stateToSave);
    };

    // Save on page visibility change (when user switches tabs or minimizes)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        saveScrollPosition();
        saveNavigationState();
      }
    };

    // Save before page unload
    const handleBeforeUnload = () => {
      saveScrollPosition();
      saveNavigationState();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      // Save when component unmounts
      saveScrollPosition();
      saveNavigationState();
    };
  }, [navigation, selectedLeadType, focusedUnitId]);

  // Update selectedHouse when houses data changes
  useEffect(() => {
    if (navigation.selectedHouse && houses.length > 0) {
      const updatedHouse = houses.find(h => h.id === navigation.selectedHouse!.id);
      if (updatedHouse) {
        setNavigation(prev => ({
          ...prev,
          selectedHouse: updatedHouse
        }));
      }
    }
  }, [houses]);

  // Restore scroll position and navigation state when component mounts
  useEffect(() => {
    const restoreState = async () => {
      if (!user?.id || isRestoringNavigation) return;

      try {
        // Restore navigation state
        const savedNavigationState = sessionStorage.getItem(NAVIGATION_STATE_KEY);
        if (savedNavigationState) {
          const parsedState = JSON.parse(savedNavigationState);
          console.log('🔄 Restoring navigation state:', parsedState);
          
          setIsRestoringNavigation(true);
          
          if (parsedState.navigation) {
            const { navigation: savedNav } = parsedState;
            
            if (savedNav.currentLevel !== 'areas' && savedNav.selectedArea) {
              // Restore deeper navigation levels
              if (savedNav.currentLevel === 'streets' || (savedNav.currentLevel === 'houses' && savedNav.selectedStreet)) {
                await loadStreets(savedNav.selectedArea.id);
              }
              
              if (savedNav.currentLevel === 'houses' && savedNav.selectedStreet) {
                await loadHouses(savedNav.selectedStreet.id);
              }
              
              if (savedNav.currentLevel === 'units' && savedNav.selectedHouse) {
                if (savedNav.selectedStreet) {
                  await loadStreets(savedNav.selectedArea.id);
                  await loadHouses(savedNav.selectedStreet.id);
                }
                await loadUnits(savedNav.selectedHouse.id);
              }
              
              setNavigation(savedNav);
            }
          }
          
          if (parsedState.selectedLeadType) {
            setSelectedLeadType(parsedState.selectedLeadType);
          }
          
          if (parsedState.focusedUnitId) {
            setFocusedUnitId(parsedState.focusedUnitId);
          }
          
          setIsRestoringNavigation(false);
        }

        // Restore scroll position after a short delay to ensure content is loaded
        setTimeout(() => {
          const savedScrollPosition = sessionStorage.getItem(SCROLL_POSITION_KEY);
          if (savedScrollPosition && scrollContainerRef.current) {
            const scrollTop = parseInt(savedScrollPosition, 10);
            scrollContainerRef.current.scrollTop = scrollTop;
            console.log('🔄 Restored scroll position:', scrollTop);
          }
        }, 100);

      } catch (error) {
        console.error('Error restoring state:', error);
        setIsRestoringNavigation(false);
      }
    };

    restoreState();
  }, [user?.id]);

  // Disabled navigation restoration to prevent loops - simple static navigation
  useEffect(() => {
    console.log('🔄 Navigation restoration disabled to prevent loops');
    setNavigationRestored(true);
  }, []);

  // Debounce ref for lead counts + initial-load tracking
  const leadCountsDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const leadCountsLoadedRef = useRef(false);
  const [isLoadingLeadCounts, setIsLoadingLeadCounts] = useState(true);

  // Load global lead counts for D2D - counts ALL active areas (matching D2DLeadsList scope)
  // useDebounce: false for initial load (immediate), true for realtime updates (debounced)
  const loadGlobalLeadCounts = useCallback(async (useDebounce = false) => {
    if (!user?.id) return;

    // Clear any pending debounced calls
    if (leadCountsDebounceRef.current) {
      clearTimeout(leadCountsDebounceRef.current);
      leadCountsDebounceRef.current = null;
    }

    // Only show loading skeletons before the first successful load
    const isInitialLoad = !leadCountsLoadedRef.current;
    if (isInitialLoad) setIsLoadingLeadCounts(true);

    const fetchData = async () => {
      try {
        // Get ALL active areas (own + shared) - matching D2DLeadsList logic
        const [ownAreasResult, sharesResult] = await Promise.all([
          supabase
            .from('d2d_areas')
            .select('id')
            .eq('created_by', user.id)
            .eq('is_active', true),
          supabase
            .from('d2d_area_shares')
            .select('area_id')
            .eq('shared_with', user.id)
            .or('is_active.eq.true,is_active.is.null')
        ]);
        
        const activeAreaIds: string[] = [
          ...(ownAreasResult.data || []).map(a => a.id),
          ...(sharesResult.data || []).map(s => s.area_id)
        ];
        
        if (activeAreaIds.length === 0) {
          setGlobalLeadCounts({ hot: 0, followup: 0, na: 0 });
          leadCountsLoadedRef.current = true;
          if (isInitialLoad) setIsLoadingLeadCounts(false);
          return;
        }
        
        // Query ALL units from ALL active areas (matching D2DLeadsList)
        const { data, error } = await supabase
          .from('d2d_units')
          .select(`
            id, tags, last_contact_at, next_reminder,
            d2d_houses!inner(
              id,
              d2d_streets!inner(id, is_active, area_id)
            )
          `)
          .in('d2d_houses.d2d_streets.area_id', activeAreaIds);

        if (error) throw error;
        const activeUnits = data || [];

        const counts = {
          hot: activeUnits.filter(unit => unit.tags?.includes('HOT')).length,
          followup: activeUnits.filter(unit => {
            if (unit.tags?.includes('WIN') || unit.tags?.includes('NA') || unit.tags?.includes('KI')) return false;
            if (unit.tags?.includes('KT')) return true;
            if (!unit.next_reminder) return false;
            
            const reminderDate = new Date(unit.next_reminder);
            const today = new Date();
            const tomorrow = new Date(today);
            tomorrow.setDate(tomorrow.getDate() + 1);
            
            return reminderDate.toDateString() === today.toDateString() || 
                   reminderDate.toDateString() === tomorrow.toDateString() || 
                   reminderDate <= today;
          }).length,
          na: activeUnits.filter(unit => {
            if (!unit.tags?.includes('NA')) return false;
            
            // Time-based visibility: units tagged NA in the morning reappear in the afternoon
            // (matching D2DLeadsList logic)
            if (unit.last_contact_at) {
              const lastContactDate = new Date(unit.last_contact_at);
              const now = new Date();
              const isToday = lastContactDate.toDateString() === now.toDateString();
              
              if (isToday) {
                const taggedHour = lastContactDate.getHours();
                const currentHour = now.getHours();
                const morningCutoff = 12;
                
                // Wenn vormittags getaggt (vor 12:00) und jetzt noch vormittags → ausblenden
                if (taggedHour < morningCutoff && currentHour < morningCutoff) {
                  return false;
                }
              }
            }
            return true;
          }).length
        };

        setGlobalLeadCounts(counts);
        leadCountsLoadedRef.current = true;
      } catch (error) {
        console.error('Error loading global lead counts:', error);
      } finally {
        // Only flip off the skeleton state for the very first load
        if (isInitialLoad) setIsLoadingLeadCounts(false);
      }
    };

    // Only debounce for realtime updates, NOT for initial load
    if (useDebounce && leadCountsLoadedRef.current) {
      leadCountsDebounceRef.current = setTimeout(fetchData, 300);
    } else {
      // Fetch immediately for initial load
      await fetchData();
    }
  }, [user?.id]);

  // Load global counts and setup SINGLE realtime subscription
  useEffect(() => {
    if (!user?.id) return;

    // Initial load (immediate, no debounce)
    loadGlobalLeadCounts(false);

    // Setup SINGLE realtime subscription for lead counts (debounced)
    const leadCountsChannel = supabase
      .channel('lead-counts-updates')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'd2d_units',
          filter: `user_id=eq.${user.id}`
        },
        () => {
          // Use debounce for realtime updates to prevent rapid UI flickering
          loadGlobalLeadCounts(true);
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'd2d_areas',
          filter: `created_by=eq.${user.id}`
        },
        () => {
          loadGlobalLeadCounts(true);
        }
      )
      .subscribe();

    // Cleanup on unmount
    return () => {
      if (leadCountsDebounceRef.current) {
        clearTimeout(leadCountsDebounceRef.current);
      }
      supabase.removeChannel(leadCountsChannel);
    };
  }, [user?.id, loadGlobalLeadCounts]);

  // Note: Lead counts now include ALL active areas (not just selected), so no need to reload on area change

  // Check for selectedLeadType from route state
  useEffect(() => {
    const routeState = location.state as { selectedLeadType?: 'hot' | 'followup' | 'na' } | null;
    if (routeState?.selectedLeadType) {
      setSelectedLeadType(routeState.selectedLeadType);
    }
  }, [location.state]);

  // Check for unit navigation from calendar or other sources
  useEffect(() => {
    const routeState = location.state as { 
      selectedUnitId?: string;
      selectedAreaId?: string;
      selectedStreetId?: string;
      selectedHouseId?: string;
    } | null;
    
    if (routeState?.selectedUnitId && routeState?.selectedAreaId && 
        routeState?.selectedStreetId && routeState?.selectedHouseId) {
      console.log('🎯 Navigating to unit from location state:', routeState);
      handleNavigateToUnit(
        routeState.selectedAreaId,
        routeState.selectedStreetId,
        routeState.selectedHouseId,
        routeState.selectedUnitId
      );
    }
  }, [location.state]);

  const handleLeadCardClick = (leadType: 'hot' | 'followup' | 'na') => {
    setSelectedLeadType(leadType);
  };

  const handleBackFromLeads = () => {
    setSelectedLeadType(null);
    
    // Find the active area from the areas list (is_active = true)
    const activeArea = areas.find(a => a.is_active === true);
    
    if (activeArea) {
      // Navigate to the active area's streets
      setNavigation({
        currentLevel: 'streets',
        selectedArea: activeArea,
        selectedStreet: undefined,
        selectedHouse: undefined,
      });
      loadStreets(activeArea.id);
    } else {
      // No active area - go back to areas list
      setNavigation({
        currentLevel: 'areas',
        selectedArea: undefined,
        selectedStreet: undefined,
        selectedHouse: undefined,
      });
    }
  };

  // Navigate directly to a specific unit from leads
  const handleNavigateToUnit = async (areaId: string, streetId: string, houseId: string, unitId: string) => {
    try {
      console.log('🎯 Navigating to unit:', { areaId, streetId, houseId, unitId });
      
      // First, reset leads view immediately
      setSelectedLeadType(null);
      
      // Find the area from current areas (includes own + shared areas)
      let area = areas.find(a => a.id === areaId);
      
      // If area not found locally (might be shared area not yet loaded), fetch from DB
      if (!area) {
        console.log('📥 Area not in local state, fetching from database...');
        const { data: areaData, error: areaError } = await supabase
          .from('d2d_areas')
          .select('*')
          .eq('id', areaId)
          .maybeSingle();
        
        if (areaError || !areaData) {
          console.error('Area not found:', areaId, areaError);
          toast({
            title: "Navigation Fehler",
            description: "Bereich nicht gefunden.",
            variant: "destructive",
          });
          return;
        }
        area = areaData;
      }

      // Load all necessary data sequentially
      console.log('📥 Loading data for navigation...');
      
      await loadStreets(areaId);
      await loadHouses(streetId);
      await loadUnits(houseId);
      
      // Fetch street and house data directly from database to ensure we have it
      // Note: RLS handles permissions, so we don't filter by user_id (supports shared areas)
      const [streetResult, houseResult] = await Promise.all([
        supabase
          .from('d2d_streets')
          .select('*')
          .eq('id', streetId)
          .maybeSingle(),
        supabase
          .from('d2d_houses')
          .select('*')
          .eq('id', houseId)
          .maybeSingle()
      ]);
      
      if (streetResult.error || !streetResult.data) {
        console.error('Failed to fetch street:', streetResult.error);
        toast({
          title: "Navigation Fehler",
          description: "Straße nicht gefunden.",
          variant: "destructive",
        });
        return;
      }
      
      if (houseResult.error || !houseResult.data) {
        console.error('Failed to fetch house:', houseResult.error);
        toast({
          title: "Navigation Fehler",
          description: "Haus nicht gefunden.",
          variant: "destructive",
        });
        return;
      }
      
      // Set navigation to final state
      console.log('✅ Setting navigation state to units');
      setNavigation({
        currentLevel: 'units',
        selectedArea: area,
        selectedStreet: streetResult.data,
        selectedHouse: houseResult.data
      });
      
      // Set the focused unit ID for auto-scrolling
      // Reset to null first to ensure useEffect triggers even for same unit ID
      setFocusedUnitId(null);
      setTimeout(() => setFocusedUnitId(unitId), 0);
      
      console.log('✅ Navigation completed successfully');
      
    } catch (error) {
      console.error('Error navigating to unit:', error);
      toast({
        title: "Navigation Fehler",
        description: "Fehler beim Navigieren zur Wohneinheit. Bitte versuchen Sie es erneut.",
        variant: "destructive",
      });
    }
  };

  // Navigation handlers - simplified without double saves
  const handleSelectArea = async (area: D2DArea) => {
    console.log('🌍 Selecting area:', area);
    setFocusedUnitId(null); // Reset focused unit on normal navigation
    
    // Check if we have preferences for this area to navigate to last visited location
    if (preferences?.last_area_id === area.id && preferences?.last_street_id) {
      console.log('🎯 Found preferences for area, navigating to last location');
      
      if (preferences.last_house_id) {
        // Navigate to last visited house/units
        console.log('🏠 Navigating to last house:', preferences.last_house_id);
        
        // Load streets and houses first
        await loadStreets(area.id);
        await loadHouses(preferences.last_street_id);
        
        // Find the street and house from loaded data
        const street = streets.find(s => s.id === preferences.last_street_id) || 
          (await supabase.from('d2d_streets').select('*').eq('id', preferences.last_street_id).eq('user_id', user!.id).maybeSingle()).data;
        
        const house = houses.find(h => h.id === preferences.last_house_id) ||
          (await supabase.from('d2d_houses').select('*').eq('id', preferences.last_house_id).eq('user_id', user!.id).maybeSingle()).data;
        
        if (street && house) {
          await loadUnits(preferences.last_house_id);
          setNavigation({
            currentLevel: 'units',
            selectedArea: area,
            selectedStreet: street,
            selectedHouse: house
          });
          return;
        }
      } else {
        // Navigate to last visited street
        console.log('🛣️ Navigating to last street:', preferences.last_street_id);
        await loadStreets(area.id);
        
        const street = streets.find(s => s.id === preferences.last_street_id) ||
          (await supabase.from('d2d_streets').select('*').eq('id', preferences.last_street_id).eq('user_id', user!.id).maybeSingle()).data;
        
        if (street) {
          await loadHouses(preferences.last_street_id);
          setNavigation({
            currentLevel: 'houses',
            selectedArea: area,
            selectedStreet: street
          });
          return;
        }
      }
    }
    
    // Default behavior: navigate to streets
    console.log('🔄 No preferences found, navigating to streets level');
    const newNavigation = {
      currentLevel: 'streets' as const,
      selectedArea: area
    };
    setNavigation(newNavigation);
    loadStreets(area.id);
  };

  const handleSelectStreet = (street: D2DStreet) => {
    console.log('🛣️ Selecting street:', street);
    setFocusedUnitId(null); // Reset focused unit on normal navigation
    const newNavigation = {
      ...navigation,
      currentLevel: 'houses' as const,
      selectedStreet: street
    };
    console.log('🔄 Setting navigation to houses level:', newNavigation);
    setNavigation(newNavigation);
    loadHouses(street.id);
  };

  const handleSelectHouse = async (house: D2DHouse) => {
    console.log('🏠 Selecting house:', house);
    setFocusedUnitId(null); // Reset focused unit on normal navigation
    
    // Load units first before changing navigation
    await loadUnits(house.id);
    
    const newNavigation = {
      ...navigation,
      currentLevel: 'units' as const,
      selectedHouse: house
    };
    console.log('🔄 Setting navigation to units level:', newNavigation);
    setNavigation(newNavigation);
  };

  const handleSelectHouseWithTagFilter = async (house: D2DHouse, tagFilter: string) => {
    console.log('🏠 Selecting house with tag filter:', house, tagFilter);
    setFocusedUnitId(null);
    
    // Load units first before changing navigation
    await loadUnits(house.id);
    
    // Save the tag filter for the unit list
    sessionStorage.setItem('initial-tag-filter', tagFilter);
    
    const newNavigation = {
      ...navigation,
      currentLevel: 'units' as const,
      selectedHouse: house
    };
    console.log('🔄 Setting navigation to units level with tag filter:', newNavigation, tagFilter);
    setNavigation(newNavigation);
  };

  const handleBackToAreas = () => {
    setNavigation({ currentLevel: 'areas' });
    // Clear preferences when going back to areas to prevent navigating to old location
    savePreferences({
      last_area_id: null,
      last_street_id: null,
      last_house_id: null,
      last_navigation_level: 'areas'
    });
  };

  const handleBackToStreets = () => {
    setNavigation({
      currentLevel: 'streets',
      selectedArea: navigation.selectedArea
    });
  };

  const handleBackToHouses = () => {
    setNavigation({
      currentLevel: 'houses',
      selectedArea: navigation.selectedArea,
      selectedStreet: navigation.selectedStreet
    });
  };

  // ==================================================================================
  // CRITICAL ACTION HANDLERS - DO NOT ADD RELOADS OR FOCUS CHANGES
  // ==================================================================================
  // These handlers update units WITHOUT triggering scrolling or focus changes
  // 
  // WHY THIS IS CRITICAL:
  // - All updates use optimistic local state updates + Realtime subscriptions
  // - NO manual reloads after operations (this would reset scroll position)
  // - NO setFocusedUnitId() calls (this would cause unwanted scrolling)
  //
  // CORRECT BEHAVIOR:
  // - User tags a unit → scroll stays exactly where it was
  // - User adds a note → scroll stays exactly where it was  
  // - User edits unit name → scroll stays exactly where it was
  // - Realtime updates apply automatically without affecting scroll
  //
  // ⚠️ DO NOT add any of these after update operations:
  //    - loadUnits() or any reload functions
  //    - setFocusedUnitId() calls
  //    - scrollToTop() or scroll manipulation
  // ==================================================================================
  
  const handleCreateStreet = async (streetData: D2DCreateStreetData) => {
    if (!navigation.selectedArea) return;
    
    const newStreet = await createStreet(navigation.selectedArea.id, streetData);
    // ✅ No reload - createStreet updates local state automatically
  };

  const handleDeleteStreet = async (streetId: string) => {
    if (!navigation.selectedArea) return;
    
    await deleteStreet(streetId);
    // ✅ No reload - deleteStreet updates local state automatically
  };

  const handleEditStreetName = async (streetId: string, newName: string) => {
    if (!navigation.selectedArea) return;
    
    await editStreetName(streetId, newName);
    // ✅ No reload - editStreetName updates local state automatically
  };

  const handleCreateHouse = async (houseData: D2DCreateHouseData) => {
    if (!navigation.selectedStreet) return null;
    
    const newHouse = await createHouse(navigation.selectedStreet.id, houseData);
    return newHouse;
  };

  const handleCreateUnit = async (unitData: any, options?: { silent?: boolean }) => {
    if (!navigation.selectedHouse) return;
    
    const newUnit = await createUnit(navigation.selectedHouse.id, unitData, options);
    if (newUnit) {
      // ✅ No reload - createUnit updates local state automatically
      
      // Auto-focus ONLY for new units (and only if they don't have NA/KI tags)
      const shouldFocusNewUnit = !unitData.tags?.includes('NA') && !unitData.tags?.includes('KI');
      if (shouldFocusNewUnit && !options?.silent) {
        setFocusedUnitId(newUnit.id);
      }
    }
  };

  const handleUpdateTags = async (unitId: string, tags: any) => {
    await updateUnitTags(unitId, tags);
    // ✅ No reload - Realtime subscription updates UI automatically
    // ⚠️ DO NOT add setFocusedUnitId() here - it would cause scrolling!
  };

  const handleAddNote = async (unitId: string, note: string) => {
    await addNote(unitId, note);
    // ✅ No reload - Realtime subscription updates UI automatically
    // ⚠️ DO NOT add setFocusedUnitId() here - it would cause scrolling!
  };

  const handleUpdateUnitName = async (unitId: string, name: string) => {
    await updateUnitName(unitId, name);
    // ✅ No reload - Realtime subscription updates UI automatically
    // ⚠️ DO NOT add setFocusedUnitId() here - it would cause scrolling!
  };

  const handleCreateFollowUp = async (unitId: string, tag: D2DTag, reminderDate: Date, notes?: string, isManual?: boolean, customerEmail?: string) => {
    await createUnitFollowUp(unitId, tag, reminderDate, notes, isManual, customerEmail);
    // ✅ No reload - Realtime subscription updates UI automatically
    // ⚠️ DO NOT add setFocusedUnitId() here - it would cause scrolling!
  };

  const handleDeleteUnit = async (unitId: string) => {
    try {
      await deleteUnit(unitId);
      // No need to reload data - deleteUnit already updates local state
      // The scroll position restoration in D2DUnitList will handle maintaining position
    } catch (error) {
      console.error('Error in handleDeleteUnit:', error);
      toast({
        title: "Fehler",
        description: "Wohneinheit konnte nicht gelöscht werden.",
        variant: "destructive",
      });
    }
  };

  const handleNavigateToCustomer = (customerId: string) => {
    setSelectedCustomerId(customerId);
    setShowCustomerDetails(true);
  };

  return (
    <SidebarProvider open={isSidebarOpen} onOpenChange={setIsSidebarOpen}>
      <div className="min-h-screen flex w-full bg-gradient-subtle min-w-0">
        {/* Sidebar */}
        <AppSidebar />
        
        {/* Main Content */}
        <div className="flex-1 flex flex-col min-w-0 max-w-full">
          {/* Top Bar */}
          <header className="h-12 flex items-center border-b bg-card/80 backdrop-blur-sm sticky top-0 z-40">
            <SidebarTrigger className="ml-2" />
            <div className="flex-1 flex items-center justify-center gap-4">
              <h1 className="text-lg font-semibold">Laufliste</h1>
              <OfflineIndicator 
                isOnline={isOnline} 
                isSyncing={isSyncing} 
                queueLength={queueLength}
              />
            </div>
            <D2DUserSettings />
          </header>

          {/* Content Area */}
          <div ref={scrollContainerRef} className="flex-1 overflow-auto">
            <div className="max-w-4xl mx-auto p-4">
              {/* Shared Dashboard Header - Clock, Timeline, Area Button, Lead Cards */}
              <DashboardHeader
                currentTime={currentTime}
                todayFollowUps={todayFollowUps as FollowUp[]}
                globalLeadCounts={globalLeadCounts}
                isLoadingLeadCounts={isLoadingLeadCounts}
                onOpenCalendar={() => setShowCalendarView(true)}
                onOpenStatistics={() => setShowStatistics(true)}
                onReorganizeFollowups={handleReorganizeFollowups}
                isReorganizingFollowups={isReorganizingFollowups}
                navigateOnLeadClick={false}
                onLeadCardClick={handleLeadCardClick}
                selectedLeadType={selectedLeadType}
                enableUnitNavigation={true}
                onNavigateToUnit={handleNavigateToUnit}
                showExtendedControls={true}
                activeArea={navigation.selectedArea || areas.find(a => a.is_active === true) || null}
                showActiveAreaButton={true}
                selectedLeadTypeLabel={selectedLeadType ? (selectedLeadType === 'hot' ? 'Hot Leads' : selectedLeadType === 'followup' ? 'Follow-ups' : 'Nicht Angetroffen') : undefined}
                onActiveAreaClick={() => {
                  const targetArea = navigation.selectedArea || areas.find(a => a.is_active === true);
                  if (targetArea) {
                    if (selectedLeadType) {
                      setSelectedLeadType(null);
                    }
                    setNavigation({
                      currentLevel: 'streets',
                      selectedArea: targetArea,
                      selectedStreet: undefined,
                      selectedHouse: undefined,
                    });
                    loadStreets(targetArea.id);
                  }
                }}
              />

        {/* Area Search - only show when on areas level */}
        {navigation.currentLevel === 'areas' && !selectedLeadType && (
          <div className="mb-4 mt-6 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Gebiet suchen (Name oder PLZ)..."
              value={areaSearchQuery}
              onChange={(e) => setAreaSearchQuery(e.target.value)}
              className="pl-10 pr-10"
            />
            {areaSearchQuery && (
              <button
                onClick={() => setAreaSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        )}

        <Separator className="mb-3" />
        {selectedLeadType ? (
          <D2DErrorBoundary>
            <D2DLeadsList
              leadType={selectedLeadType}
              onBack={handleBackFromLeads}
              onNavigateToUnit={handleNavigateToUnit}
              onTagChange={loadGlobalLeadCounts}
            />
          </D2DErrorBoundary>
        ) : (
          <>
            {/* Content based on current level */}
            {navigation.currentLevel === 'areas' && (
              <D2DAreaList
                areas={areaSearchQuery 
                  ? areas.filter(area => 
                      area.name.toLowerCase().includes(areaSearchQuery.toLowerCase()) ||
                      (area.plz && area.plz.includes(areaSearchQuery))
                    )
                  : areas}
                loading={loading}
                onSelectArea={handleSelectArea}
                onAreasUpdated={() => {
                  console.log('🔄 Reloading areas after change...');
                  loadAreas();
                  loadGlobalLeadCounts();
                  loadTodayFollowUps();
                }}
                onAreaDeactivated={() => {
                  console.log('🔄 Area changed, resetting navigation and reloading data...');
                  setNavigation({ currentLevel: 'areas' });
                  // Clear preferences for the deactivated area
                  savePreferences({
                    last_area_id: null,
                    last_street_id: null,
                    last_house_id: null,
                    last_navigation_level: 'areas'
                  });
                  // Reload data for the new active area
                  loadAreas();
                  loadGlobalLeadCounts();
                  loadTodayFollowUps();
                }}
                cacheArea={cacheArea}
                clearCache={clearCache}
                isCaching={isCaching}
                getUsersInArea={getUsersInArea}
              />
            )}

            {navigation.currentLevel === 'streets' && (
              <D2DStreetList
                streets={streets}
                loading={loading}
                lastPlz={preferences?.last_plz}
                area={navigation.selectedArea ? {
                  name: navigation.selectedArea.name,
                  plz: navigation.selectedArea.plz,
                  is_active: navigation.selectedArea.is_active
                } : null}
                onSelectStreet={handleSelectStreet}
                onCreateStreet={handleCreateStreet}
                onDeleteStreet={handleDeleteStreet}
                onEditStreetName={handleEditStreetName}
                onBack={handleBackToAreas}
                getUsersInStreet={getUsersInStreet}
              />
            )}

            {navigation.currentLevel === 'houses' && (
              <D2DHouseList
                houses={houses}
                loading={loading}
                street={navigation.selectedStreet ? {
                  name: navigation.selectedStreet.name,
                  plz: navigation.selectedStreet.plz
                } : null}
                onSelectHouse={handleSelectHouse}
                onSelectHouseWithTagFilter={handleSelectHouseWithTagFilter}
                onCreateHouse={handleCreateHouse}
                onCreateUnit={async (houseId, unitData) => {
                  await createUnit(houseId, unitData);
                }}
                onDeleteHouse={deleteHouse}
                onBack={handleBackToStreets}
                isAreaInactive={!navigation.selectedArea?.is_active}
                getUsersInHouse={getUsersInHouse}
                onAddHouseNote={async (houseId, note) => {
                  await addHouseNote(houseId, note);
                  // Reload houses to show updated notes
                  if (navigation.selectedStreet?.id) {
                    await loadHouses(navigation.selectedStreet.id);
                  }
                }}
                onUpdateHouseNote={async (noteId, note) => {
                  await updateHouseNote(noteId, note);
                  // Reload houses to show updated notes
                  if (navigation.selectedStreet?.id) {
                    await loadHouses(navigation.selectedStreet.id);
                  }
                }}
                onDeleteHouseNote={async (houseId, noteId) => {
                  await deleteHouseNote(houseId, noteId);
                  // Reload houses to show updated notes
                  if (navigation.selectedStreet?.id) {
                    await loadHouses(navigation.selectedStreet.id);
                  }
                }}
              />
            )}

            {navigation.currentLevel === 'units' && (
              <D2DErrorBoundary>
                <D2DUnitList
                  units={units}
                  loading={loading}
                  focusedUnitId={focusedUnitId}
                  location={navigation.selectedStreet && navigation.selectedHouse ? {
                    street: navigation.selectedStreet.name,
                    plz: navigation.selectedStreet.plz,
                    houseNumber: navigation.selectedHouse.house_number
                  } : null}
                  house={navigation.selectedHouse}
                  onCreateUnit={handleCreateUnit}
                  onCreateUnitsBatch={navigation.selectedHouse ? async (units, onProgress) => {
                    return await createUnitsBatch(navigation.selectedHouse!.id, units, onProgress);
                  } : undefined}
                  onUpdateTags={handleUpdateTags}
                  onUpdateTagsOnly={updateUnitTagsOnly}
                  onAddNote={handleAddNote}
                  onDeleteNote={deleteNote}
                  onUpdateUnitName={handleUpdateUnitName}
                  onCreateFollowUp={handleCreateFollowUp}
                  onDeleteUnit={handleDeleteUnit}
                  onBack={handleBackToHouses}
                  onNavigateToCustomer={handleNavigateToCustomer}
                  onRefreshUnits={loadUnits}
                  isAreaInactive={!navigation.selectedArea?.is_active}
                  onAddHouseNote={async (houseId, note) => {
                    await addHouseNote(houseId, note);
                    // Reload houses to update the house prop with new notes
                    if (navigation.selectedStreet?.id) {
                      await loadHouses(navigation.selectedStreet.id);
                      
                      // Update selectedHouse with the latest data from houses
                      const updatedHouse = houses.find(h => h.id === houseId);
                      if (updatedHouse) {
                        setNavigation(prev => ({
                          ...prev,
                          selectedHouse: updatedHouse
                        }));
                      }
                    }
                  }}
                  onUpdateHouseNote={async (noteId, note) => {
                    await updateHouseNote(noteId, note);
                    // Reload houses to update the house prop with updated notes
                    if (navigation.selectedStreet?.id && navigation.selectedHouse?.id) {
                      await loadHouses(navigation.selectedStreet.id);
                      
                      // Update selectedHouse with the latest data from houses
                      const updatedHouse = houses.find(h => h.id === navigation.selectedHouse!.id);
                      if (updatedHouse) {
                        setNavigation(prev => ({
                          ...prev,
                          selectedHouse: updatedHouse
                        }));
                      }
                    }
                  }}
                  onDeleteHouseNote={async (houseId, noteId) => {
                    await deleteHouseNote(houseId, noteId);
                    // Reload houses to update the house prop with remaining notes
                    if (navigation.selectedStreet?.id) {
                      await loadHouses(navigation.selectedStreet.id);
                      
                      // Update selectedHouse with the latest data from houses
                      const updatedHouse = houses.find(h => h.id === houseId);
                      if (updatedHouse) {
                        setNavigation(prev => ({
                          ...prev,
                          selectedHouse: updatedHouse
                        }));
                      }
                    }
                  }}
                />
              </D2DErrorBoundary>
            )}
          </>
        )}
            </div>
          </div>
        </div>
      </div>

      {/* Customer Details Modal */}
      {showCustomerDetails && selectedCustomerId && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-background rounded-lg shadow-lg w-full max-w-4xl max-h-[90vh] overflow-hidden">
            <CustomerManagement 
              customerId={selectedCustomerId} 
              onClose={() => {
                setShowCustomerDetails(false);
                setSelectedCustomerId(null);
              }} 
            />
          </div>
        </div>
      )}

      {/* Calendar View Modal */}
      {user && (
        <D2DFollowUpCalendar 
          open={showCalendarView}
          onOpenChange={setShowCalendarView}
          userId={user.id}
        />
      )}

      {/* Success Statistics Modal */}
      {user && (
        <D2DSuccessStatistics
          open={showStatistics}
          onOpenChange={setShowStatistics}
        />
      )}
    </SidebarProvider>
  );
}